<template>
    <ArboTooltip
        v-if="tooltipContent"
        :content="tooltipContent"
        :class="[woohooProgressStyleComputed['--type']]"
        :delay="[300, 0]"
        :style="woohooProgressStyleComputed">
        <slot>
            <div class="actual-woohoo progress"></div>
            <div class="expected-woohoo progress"></div
        ></slot>
    </ArboTooltip>
</template>
<script setup lang="ts">
type Props = {
    actual: number;
    expected: number;
    lastUpdate: string;
    unit: string;
    forceComplete?: boolean;
};
const props = defineProps<Props>();
import { h, render, computed } from 'vue';
import ProgressTooltip from './ProgressTooltip.vue';

const tooltipContent = computed(() => {
    const params = {
        data: [
            {
                name: 'Woohoo',
                percent: props.actual,
                color: woohooProgressStyleComputed.value['--actual-color'],
                key: 'progress1'
            },
            {
                name: 'Expected Woohoo',
                percent: props.expected,
                color: woohooProgressStyleComputed.value['--expected-color'],
                key: 'progress2'
            }
        ],
        unit: props.unit,
        lastUpdate: props.lastUpdate
    };
    const renderAndGetHtml = (component: any) => {
        const container = document.createElement('div');
        render(h(component, params), container);
        return container.innerHTML;
    };
    if (!props.lastUpdate) {
        return undefined;
    }
    return renderAndGetHtml(ProgressTooltip);
});

const woohooProgressStyleComputed = computed(() => {
    return {
        ...woohooProgressStyle({
            expected: Number(props.expected),
            actual: Number(props.actual)
        })
    };
});
const colorCode = {
    GREEN: '#02C39A',
    RED_100: '#FF5200',
    RED_200: '#FFB18D',
    GREY: '#515151',
    BLUE_200: '#A3D4F5',
    BLUE_100: '#3C9BFA'
};
function woohooProgressStyle(data: { expected: number; actual: number }) {
    let defaultColor = '#F3F5F8',
        expectedColor = '#FFB099', // RED200
        actualColor = '#FF5200',
        expectedWidth = Number.isNaN(data.expected) ? 0 : data.expected,
        expectedZIndex = 1,
        actualZIndex = 1,
        type = 'complete';

    switch (true) {
        case Number(data.actual) >= 100 || props.forceComplete: {
            expectedColor = defaultColor;
            actualColor = colorCode.GREEN;
            expectedZIndex = 1;
            actualZIndex = 2;
            type = 'complete';
            break;
        }
        case Number.isNaN(data.expected): {
            //No expected woohoo

            expectedColor = defaultColor;
            actualColor = colorCode.GREY;
            expectedZIndex = 1;
            actualZIndex = 2;
            type = 'incomplete';
            break;
        }
        case Number(data.expected) < Number(data.actual): {
            // Ahead & On-time

            expectedColor = colorCode.BLUE_200;
            actualColor = colorCode.BLUE_100;
            expectedZIndex = 2;
            actualZIndex = 1;
            type = 'beyond-expectation';
            break;
        }
        case Number(data.expected) > Number(data.actual): {
            //late
            expectedZIndex = 1;
            actualZIndex = 2;
            expectedColor = colorCode.RED_200;
            actualColor = colorCode.RED_100;
            type = 'beyond-expectation';
            break;
        }
    }

    return {
        '--default-color': defaultColor,
        '--expected-color': expectedColor,
        '--actual-color': actualColor,
        '--expected-width': `${expectedWidth}%`,
        '--actual-width': `${data.actual}%`,
        '--expected-z-index': expectedZIndex,
        '--actual-z-index': actualZIndex,
        '--type': type
    };
}
</script>
